import { computed, reactive, ComputedRef } from 'vue'
import type { IConnackPacket } from 'mqtt'

import { ParcelType } from '@/constants'
import { Parcel, User } from '@/common/classes'
import useMqttService from '@/composables/mqtt'

// Upon message received, call store functions from App.vue

const mqttService = useMqttService()
const showConsoleLogs = import.meta.env.VITE_SHOW_LOGS || false
// Register a callback to this to receive new messages in the app
let newMessageCB: ((topic: string, payload: Buffer) => void) | undefined = undefined

interface State {
  parcelType: ParcelType
  logs: Record<string, unknown>
  logIDs: []
  lastSentParcel?: Parcel
}

const _state: State = reactive({
  parcelType: ParcelType.GlobalStatus,
  task: undefined,
  message: undefined,
  logs: {},
  logIDs: [],
  lastSentParcel: undefined,
})

interface Getters {
  mqttConnected: ComputedRef<boolean>
  lastSentParcel: ComputedRef<Parcel | undefined>
}

interface Actions {
  setNewMessageCallback: (fn: (topic: string, payload: Buffer) => void) => void
  openMQTTConnection: (user: User) => Promise<void>
  closeMQTTConnection: () => Promise<void>
  subscribeTopic: (topic: string) => Promise<void>
  unSubscribeTopic: (topic: string) => Promise<void>
  sendParcel: (topic: string, parcel: Parcel) => Promise<void>
}

interface ServiceInterface {
  state: State
  getters: Getters
  actions: Actions
}

function useParcelStore(): ServiceInterface {
  const getters = {
    get mqttConnected(): ComputedRef<boolean> {
      return computed(() => mqttService.connected())
    },
    get lastSentParcel(): ComputedRef<Parcel | undefined> {
      return computed(() => _state.lastSentParcel)
    },
  }

  const actions = {
    setNewMessageCallback: (fn: (topic: string, payload: Buffer) => void): void => {
      newMessageCB = fn
    },
    openMQTTConnection: (user: User): Promise<void> => {
      return new Promise((resolve, reject) => {
        try {
          if (!mqttService.connected()) {
            const connectionCallback = (data?: IConnackPacket, error?: Error): void => {
              if (error) {
                if (showConsoleLogs) console.log(error)
                reject(error)
              } else resolve()
            }

            const newMessageCallback = (topic: string, payload: Buffer): void => {
              if (newMessageCB) newMessageCB(topic, payload)
              else if (showConsoleLogs) console.log('Message received but no callback defined to manage it')
            }

            const catchCallback = (message: string): void => {
              console.warn(`catched mqtt event: ${message}`)
              // TODO: Why was the page being reloaded here?
              // window.location.reload() // reload wall page
            }

            const userId = user._id
            let mqttUsername = import.meta.env.MOSQUITTO_USER
            let mqttPassword = import.meta.env.MOSQUITTO_PASSWORD
            if (user.mqtt) {
              // replace values (if existing)
              if (user.mqtt.username) mqttUsername = user.mqtt.username
              if (user.mqtt.password) mqttPassword = user.mqtt.password
            }
            if (mqttUsername && mqttPassword)
              return mqttService.initializeClientConnection({
                userId,
                mqttUsername,
                mqttPassword,
                connectionCallback,
                newMessageCallback,
                catchCallback,
              })
            else reject(new Error('Username or password missing'))
          } else resolve() // mqttService is active and nothing needs to be done
        } catch (e) {
          reject(new Error(`Error during mqtt connection establishment: ${e}`))
        }
      })
    },
    closeMQTTConnection: async (): Promise<void> => {
      try {
        await mqttService.closeClientConnection()
      } catch (error) {
        console.log(error)
      }
    },
    subscribeTopic: async (topic: string) => {
      try {
        const granted = await mqttService.subscribe(topic)
        if (granted.length === 0) throw new Error('Subscription failed')
        else if (showConsoleLogs) console.log(`Subscribed ${topic}`)
      } catch (error) {
        console.log(error)
      }
    },
    unSubscribeTopic: async (topic: string) => {
      try {
        if (mqttService.connected()) {
          await mqttService.unSubscribe(topic)
          if (showConsoleLogs) console.log(`Unsubscribed ${topic}`)
        }
      } catch (error) {
        console.error(error)
      }
    },
    sendParcel: async (topic: string, parcel: Parcel) => {
      try {
        await mqttService.publish(topic, parcel.serialised())
        if (showConsoleLogs) console.log(`Sent parcel ${parcel.parcelType.toString()}`)
        _state.lastSentParcel = parcel
      } catch (error) {
        console.error(error)
      }
    },
  }

  const state = _state

  return { state, getters, actions }
}

export default useParcelStore
